
import { defineComponent } from '@nuxtjs/composition-api';
import prepareDynamicBlock from '~/helpers/prepareDynamicBlockFrontkit';
import kebabToPascal from '~/helpers/kebabToPascal';
import useSectionsPreloadData from '~/domains/constructor-pages/useSectionsPreloadData';
import {
  forEachSections,
  sectionsWithDataProps as abSectionsWithDataProps,
} from '@webplatform/asbis-ui/dist/sections';
import {
  RawBlock,
  PrepareDynamicBlock,
  PreparedBlock,
  UseSectionsPreloadDataProps,
} from '~/domains/constructor-pages/types';

export default defineComponent({
  props: {
    blocks: {
      type: Array as () => RawBlock[],
      required: true,
    },
  },
  setup(props) {
    const CANYON_COMPONENTS_WITH_DATA_PROPS = [
      'AboutCards',
      'AboutGaming',
      'AboutHeader',
      'AboutImage',
      'AboutText',
      'AppPromotions',
      'CategoryBannerLarge',
      'CategoryBannerSmall',
      'CategoryBannerSmallGroup',
      'CategoryCardsSection',
      'FormSection',
      'MainSlider',
      'NewsSection',
      'ProductCharacteristics',
      'ProseBlock',
      'RichAdvantages',
      'RichAdvantagesList',
      'RichFooter',
      'RichHeader',
      'RichImagesGrid',
      'RichVideo',
      'SeoText',
      'TipsSection',
      'UserHelpCards',
    ];

    const COMPONENTS_ALIASES: Record<string, string> = {
      'html-content-block': 'prose-block',
      red_block: 'rich-header',
      gallery: 'rich-images-grid',
      chess_gallery: 'rich-advantages-list',
      youtube_video: 'rich-video',
      footer: 'rich-footer',
      'tips-section': 'tips-section',
    };

    const EXCLUDED_BLOCKS = ['page-info', 'short_description'];

    const LOCAL_SECTIONS_WITH_DATA_PROPS = [
      'HtmlContentBlock',
      'Red_block',
      'Gallery',
      'Chess_gallery',
      'Youtube_video',
      'Footer',
      ...CANYON_COMPONENTS_WITH_DATA_PROPS,
    ];

    const localSections: Record<string, any> = {};
    const asbisUiSections: Record<string, any> = {};

    forEachSections((sectionName, sectionComponent) => {
      if (!(sectionName in localSections)) {
        asbisUiSections[sectionName] = sectionComponent;
      }
    });

    const sectionComponents = {
      ...localSections,
      ...asbisUiSections,
    };

    const sectionsWithDataProps = abSectionsWithDataProps.concat(
      LOCAL_SECTIONS_WITH_DATA_PROPS,
    );

    const wrappedPrepareDynamicBlock: PrepareDynamicBlock = (block: RawBlock) =>
      prepareDynamicBlock(block, {
        excludedBlocks: EXCLUDED_BLOCKS,
        aliases: COMPONENTS_ALIASES,
        sectionsWithDataProps,
      }) as PreparedBlock;

    const { enhancedComponents } = useSectionsPreloadData(
      props as UseSectionsPreloadDataProps,
      wrappedPrepareDynamicBlock,
    );

    const getComponentToRender = (componentType?: string) => {
      return componentType
        ? sectionComponents[kebabToPascal(componentType)] ||
            kebabToPascal(componentType)
        : undefined;
    };

    const getBindingData = (component: PreparedBlock) => {
      return component?.data || {};
    };

    return {
      enhancedComponents,
      getComponentToRender,
      getBindingData,
    };
  },
});
